import { Download, RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { Button, ConfirmDialog, cx, ErrorNote, Spinner, toast } from "../../../components/ui";
import { api } from "../../../lib/api";

export interface VersionInfo {
  version: number;
  at: string;
  by: string;
  note?: string;
}
interface Change {
  where: string;
  change: "added" | "removed" | "changed" | "moved";
  what?: string;
  from?: unknown;
  to?: unknown;
}

const show = (v: unknown) => (v === undefined ? "—" : typeof v === "string" ? v : JSON.stringify(v));

/**
 * Versions (docs/phase3-plan.md §4): every saved version, a compare of any two (or a version and the working draft),
 * restore of an earlier version as the draft, and export of the whole design to move it to another portal.
 */
export function VersionsPanel({ id, versions, current, hasDraft, rev, onRestored }: { id: string; versions: VersionInfo[]; current: number; hasDraft: boolean; rev: number; onRestored: () => void }) {
  // With a draft: what saving it would change against the current version; without: the last two versions.
  const [from, setFrom] = useState(String(hasDraft ? current : Math.max(1, current - 1)));
  const [to, setTo] = useState(hasDraft ? "draft" : String(current));
  const [changes, setChanges] = useState<Change[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [restore, setRestore] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setChanges(null);
    setError(null);
    if (from === to) return setChanges([]);
    api<{ changes: Change[] }>(`/apps/ja/automations/${id}/compare?from=${from}&to=${to}`)
      .then((r) => setChanges(r.changes))
      .catch((e) => setError((e as Error).message));
  }, [id, from, to, rev]);

  const download = async () => {
    setExporting(true);
    try {
      const bundle = await api<{ type: string; current: number }>(`/apps/ja/automations/${id}/export`);
      const url = URL.createObjectURL(new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${bundle.type}-v${bundle.current}-export.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setExporting(false);
    }
  };

  const choices = [...versions.map((v) => ({ value: String(v.version), label: `v${v.version}${v.version === current ? " (current)" : ""}` })), ...(hasDraft ? [{ value: "draft", label: "Working draft" }] : [])];
  const small = "h-8 rounded-lg border border-line bg-surface px-2 text-sm";
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
      <div className="space-y-3">
        <div className="rounded-xl border border-line bg-surface divide-y divide-line">
          {[...versions].reverse().map((v) => (
            <div key={v.version} className="px-3 py-2 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-ink">
                  v{v.version}
                  {v.version === current && <span className="ml-2 text-xs text-good font-normal">current: runs use it</span>}
                </span>
                {v.version !== current && (
                  <button type="button" className="inline-flex items-center gap-1 text-xs text-accent-text hover:underline" onClick={() => setRestore(v.version)}>
                    <RotateCcw className="size-3" aria-hidden /> Restore as draft
                  </button>
                )}
              </div>
              <div className="text-xs text-ink-2">{v.note || "—"}</div>
              <div className="text-xs text-muted">
                {new Date(v.at).toLocaleString()} · {v.by}
              </div>
            </div>
          ))}
        </div>
        <Button onClick={() => void download()} busy={exporting} title="Every version as one file, to import into another portal">
          <Download className="size-4" aria-hidden /> Export the design
        </Button>
        <p className="text-xs text-muted">To copy this automation to the production portal, export it here and use Import a design there.</p>
      </div>

      <div className="rounded-xl border border-line bg-surface p-4 space-y-3 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-semibold text-ink mr-2">Compare</h3>
          <select className={small} value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From">
            {choices.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          <span className="text-muted">to</span>
          <select className={small} value={to} onChange={(e) => setTo(e.target.value)} aria-label="To">
            {choices.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        {error && <ErrorNote>{error}</ErrorNote>}
        {!changes && !error ? (
          <Spinner />
        ) : changes && !changes.length ? (
          <p className="text-muted">{from === to ? "Choose two different versions." : "No differences."}</p>
        ) : (
          changes && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-muted border-b border-line">
                  <tr>
                    <th className="px-2 py-1 font-medium">Where</th>
                    <th className="px-2 py-1 font-medium">Change</th>
                    <th className="px-2 py-1 font-medium">Before</th>
                    <th className="px-2 py-1 font-medium">After</th>
                  </tr>
                </thead>
                <tbody>
                  {changes.map((c, i) => (
                    <tr key={i} className="border-b border-line last:border-0 align-top">
                      <td className="px-2 py-1 font-mono whitespace-nowrap">{c.where}</td>
                      <td className={cx("px-2 py-1 whitespace-nowrap", c.change === "added" ? "text-good" : c.change === "removed" ? "text-bad" : "text-ink-2")}>
                        {c.change}
                        {c.what ? ` ${c.what}` : ""}
                      </td>
                      <td className="px-2 py-1 font-mono break-all max-w-xs">{show(c.from)}</td>
                      <td className="px-2 py-1 font-mono break-all max-w-xs">{show(c.to)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {restore !== null && (
        <ConfirmDialog
          title={`Restore v${restore} as the draft?`}
          confirm="Restore"
          onConfirm={async () => {
            await api(`/apps/ja/automations/${id}/restore`, { body: { version: restore, rev } });
            toast(`v${restore} is now the working draft. Save it as a version to make it current.`);
            onRestored();
          }}
          onClose={() => setRestore(null)}
        >
          <p className="text-sm text-ink-2">
            {hasDraft ? "This replaces the working draft and its unsaved changes. " : ""}Runs keep using v{current} until you save the draft as a version.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}
